import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2 } from 'lucide-react';
import { authAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

/**
 * AuthCallback - Handles Google OAuth redirect
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 * 
 * Flow:
 * 1. User clicks "Continue with Google" on login page
 * 2. Redirected to Emergent Auth -> Google OAuth
 * 3. Redirected back here with session_id in URL fragment
 * 4. Exchange session_id for JWT token
 * 5. Store token and redirect to dashboard
 */
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL fragment (hash)
        const hash = location.hash;
        const params = new URLSearchParams(hash.replace('#', ''));
        const sessionId = params.get('session_id');

        if (!sessionId) {
          toast.error('Authentication failed - no session ID');
          navigate('/partners/login');
          return;
        }

        // Exchange session_id for JWT token via backend
        const response = await authAPI.googleSession({ session_id: sessionId });
        
        const { 
          token, 
          partner_id, 
          partner_short_id,
          is_admin, 
          business_name, 
          subscription_plan,
          effective_plan,
          is_new_user,
          profile_complete
        } = response.data;

        // Store auth data
        login({ partner_id, partner_short_id, is_admin, business_name, subscription_plan, effective_plan }, token);

        // Show appropriate message
        if (is_new_user) {
          toast.success('Welcome to Nanokart.ai! You received ₹500 free usage balance.');
        } else {
          toast.success('Welcome back!');
        }

        // Redirect based on profile completion and admin status
        if (is_admin) {
          navigate('/admin', { replace: true });
        } else if (!profile_complete) {
          // Redirect to settings to complete profile
          toast('Please complete your profile to continue', { icon: '📝' });
          navigate('/partners/settings?complete_profile=true', { replace: true });
        } else {
          navigate('/partners', { replace: true });
        }
      } catch (error) {
        console.error('Google auth error:', error);
        toast.error(error.response?.data?.detail || 'Authentication failed');
        navigate('/partners/login', { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, login]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="text-center"
      >
        <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/25">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-slate-900 mb-2">Completing sign in...</h2>
        <p className="text-slate-500">Please wait while we verify your account</p>
      </motion.div>
    </div>
  );
};

export default AuthCallback;
